/**
 * `useContextPaneCommandsFromAgent` — case-context navigation +
 * pane tab focus `lx_*` tools from the concierge.
 *
 * Concierge slash-commands Part A §2.3 (handoff
 * `concierge-slash-commands-lexide-tools-handoff-2026-06-30.md`).
 *
 * Wires (mounted at App level — these all work from any route):
 *   • `lx_open_client {clientId}`         → selectClient (N3: in-place scope)
 *   • `lx_open_matter {clientId, matterId}` → selectMatter (N3: in-place scope)
 *   • `lx_open_case {caseId}`             → navigate (resolves the
 *      client/matter chain from the active selection if it matches,
 *      otherwise navigates to the case-resolver path which lets the
 *      router pick the chain). The `lx_open_case` runtime contract
 *      specifies a bare `caseId` (per parent plan §13.1), so we
 *      use the existing `/cases/:caseId/*` route that
 *      `CaseRouteResolver` already owns (App.tsx:460).
 *   • `lx_focus_context_tab {tab}`        → useContextPaneStore.setActiveTab.
 *      Reads `useActiveContextStore.selection.scope` to determine
 *      which scope to switch (no-op when no scope is selected).
 *
 * **NOT in this hook** (deferred to Batch 3 / store-gap decisions):
 *   • `lx_run_briefing` — the runtime tool's per-tier dispatch isn't
 *      cleanly served by the existing `ChatBriefingButton` (the
 *      button only handles client + matter tiers; case-tier briefing
 *      runs through the in-case chat session, not a separate
 *      action). Wiring it requires a design call (synthesise a
 *      prompt into the case chat? require an explicit tier in
 *      toolInput?). Flagged in the handback.
 *
 * Contract: act on `phase === "start"` only; accept legacy field
 * names. Pure handler `handleContextPaneCommand` extracted for tests.
 */

import { useNavigate } from "react-router-dom";
import { useEventStream } from "./useEventStream";
import { useContextPaneStore, type PaneTab } from "@/stores/useContextPaneStore";
import {
  useActiveContextStore,
  type ActiveScope,
} from "@/stores/useActiveContextStore";

export interface AgentEventPayloadRuntime {
  data?: {
    phase?: string;
    toolName?: string;
    name?: string;
    toolInput?: Record<string, unknown>;
    args?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

const PANE_TABS: readonly PaneTab[] = ["client", "matter", "case"];

function isPaneTab(v: unknown): v is PaneTab {
  return typeof v === "string" && (PANE_TABS as readonly string[]).includes(v);
}

/** Strict-ish id shape — case ids are typically alnum + `-` + `_`. We
 *  do NOT URI-encode the segment; the router treats it as opaque.
 *  Reject anything containing a path separator. */
function isSafeId(v: unknown): v is string {
  return (
    typeof v === "string" &&
    v.length > 0 &&
    !v.includes("/") &&
    !v.includes("\\") &&
    !v.includes("..")
  );
}

export interface ContextPaneCommandDeps {
  navigate: (path: string) => void;
  /** N3: the detail pages are retired — "open the client/matter" means
   *  selecting the scope; the panes + context tabs reflect it in place. */
  selectClient: (clientId: string) => void;
  selectMatter: (clientId: string, matterId: string) => void;
  setActiveTab: (scope: ActiveScope, tab: PaneTab) => void;
  /** Current pane scope, read from useActiveContextStore — used to
   *  decide which scope `lx_focus_context_tab` should mutate. Null
   *  when the lawyer isn't currently in a case/matter/client. */
  paneScope: ActiveScope | null;
}

export function handleContextPaneCommand(
  payload: unknown,
  deps: ContextPaneCommandDeps,
): void {
  const typed = payload as AgentEventPayloadRuntime;
  if (typed.data?.phase !== "start") return;
  const tool = typed.data?.toolName ?? typed.data?.name;
  const input = typed.data?.toolInput ?? typed.data?.args;

  switch (tool) {
    case "lx_open_client": {
      const clientId = input?.clientId;
      if (!isSafeId(clientId)) return;
      // N3: detail pages retired — selecting the scope IS "open the client";
      // the panes + context tabs reflect it wherever the user is.
      deps.selectClient(clientId);
      return;
    }
    case "lx_open_matter": {
      const clientId = input?.clientId;
      const matterId = input?.matterId;
      if (!isSafeId(clientId) || !isSafeId(matterId)) return;
      // N3: detail pages retired — selecting the scope IS "open the matter".
      deps.selectMatter(clientId, matterId);
      return;
    }
    case "lx_open_case": {
      // The runtime contract is a bare `caseId`; the App.tsx route
      // `/cases/:caseId/*` is owned by `CaseRouteResolver` which
      // resolves the client/matter chain on landing. No need to
      // re-thread the chain here.
      const caseId = input?.caseId;
      if (!isSafeId(caseId)) return;
      deps.navigate(`/cases/${caseId}`);
      return;
    }
    case "lx_focus_context_tab": {
      const tab = input?.tab;
      if (!isPaneTab(tab)) return;
      // No-op when the lawyer isn't in a scope (the pane isn't
      // mounted). The runtime tool fired anyway is harmless.
      if (!deps.paneScope) return;
      deps.setActiveTab(deps.paneScope, tab);
      return;
    }
    default:
      return;
  }
}

export function useContextPaneCommandsFromAgent(): void {
  const navigate = useNavigate();
  const setActiveTab = useContextPaneStore((s) => s.setActiveTab);
  const paneScope = useActiveContextStore((s) => s.getPaneScope());

  useEventStream("session.tool", (payload) => {
    const { selectClient, selectMatter } = useActiveContextStore.getState();
    handleContextPaneCommand(payload, { navigate, selectClient, selectMatter, setActiveTab, paneScope });
  });
}
